
import React from "react";
import { Shell } from "@/components/layout/Shell";
import { GroupForm, GroupFormValues } from "@/components/groups/GroupForm";
import { useGroups } from "@/hooks/useGroups";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function GroupEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const { 
    useGroupQuery, 
    useUpdateGroupMutation 
  } = useGroups();
  
  const { data: group, isLoading, error } = useGroupQuery(id || "");
  const updateGroupMutation = useUpdateGroupMutation();

  const handleSubmit = (values: GroupFormValues) => {
    if (!id || !group) return;
    
    updateGroupMutation.mutate(
      {
        id,
        name: values.name,
        description: values.description,
        user_id: group.user_id,
        created_at: group.created_at,
        updated_at: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          navigate("/groups");
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Shell className="p-4 md:p-6">
        <h1 className="text-3xl font-bold tracking-tight mb-6">Edit Group</h1>
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/4" />
          <Skeleton className="h-32 w-full" />
          <div className="flex justify-end">
            <Skeleton className="h-10 w-24" />
          </div>
        </div>
      </Shell>
    );
  }

  if (error || !group) {
    return (
      <Shell className="p-4 md:p-6">
        <h1 className="text-3xl font-bold tracking-tight mb-6">Edit Group</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error ? `Error loading group: ${error}` : "Group not found"}
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const defaultValues = {
    name: group.name,
    description: group.description || "",
  };

  return (
    <Shell className="p-4 md:p-6">
      <h1 className="text-3xl font-bold tracking-tight mb-6">Edit Group</h1>
      <GroupForm 
        onSubmit={handleSubmit} 
        defaultValues={defaultValues}
        isLoading={updateGroupMutation.isPending}
        mode="edit"
        group={group}
      />
    </Shell>
  );
}
